import { ApolloClient, NormalizedCacheObject, useApolloClient, useQuery } from '@apollo/client'
import React from 'react'

import {
  FetchReviewProductGroupQuery,
  FetchReviewProductGroupQueryVariables,
} from '../../__generated__/graphql/catalog/graphql'
import { reportClientError } from '../../app/_components/chrome/scripts/DataDogRumScript'
import { QueryStatus } from '../../lib/hooks/types'

import { FETCH_REVIEW_PRODUCT_GROUP } from './queries'

export type ReviewsProductGroupQueryData = {
  product: {
    color: string
    handle: string
    seoTitle: string | null
    shopifyId: string
    title: string
    images: {
      nodes: {
        source: string
      }[]
    }
    productGroup: {
      handle: string
    }
  } | null
}

export type UseReviewsProductGroupDataOutput = {
  data: {
    product: {
      color: string
      handle: string
      seoTitle: string | null
      shopifyId: string
      title: string
      image: string
    }
    productGroup: {
      handle: string
    }
  } | null
  error: Error | undefined
  status: QueryStatus
}

export const primeClientForReviewsProductGroupData = ({
  client,
  handle,
}: {
  client: ApolloClient<NormalizedCacheObject>
  handle: string
}) => {
  return client.query<FetchReviewProductGroupQuery, FetchReviewProductGroupQueryVariables>({
    query: FETCH_REVIEW_PRODUCT_GROUP,
    variables: { handle },
  })
}

export const useReviewsProductGroupData = (
  handle: string | undefined
): UseReviewsProductGroupDataOutput => {
  const apolloClient = useApolloClient()
  const ssrData = apolloClient.readQuery<ReviewsProductGroupQueryData>({
    query: FETCH_REVIEW_PRODUCT_GROUP,
    variables: { handle },
  })

  const { data, called, loading, error, previousData } = useQuery<ReviewsProductGroupQueryData>(
    FETCH_REVIEW_PRODUCT_GROUP,
    {
      variables: { handle },
      onError(error) {
        reportClientError({
          error,
          context: {
            scope: 'Reviews Page',
            label: 'query failed',
          },
        })
      },
    }
  )

  const product = React.useMemo(() => {
    return data?.product ?? previousData?.product ?? ssrData?.product ?? null
  }, [data?.product, previousData?.product, ssrData?.product])

  const status = React.useMemo<QueryStatus>(() => {
    if (loading) return 'pending'
    if (error) return 'rejected'
    if (!called && !product) return 'idle'
    return 'resolved'
  }, [called, error, loading, product])

  return React.useMemo<UseReviewsProductGroupDataOutput>(() => {
    const data = product
      ? {
          product: {
            handle: product.handle,
            color: product.color,
            image: product.images.nodes[0]!.source,
            seoTitle: product.seoTitle,
            shopifyId: product.shopifyId,
            title: product.title,
          },
          productGroup: {
            handle: product.productGroup.handle,
          },
        }
      : null

    return {
      data,
      status,
      error,
    }
  }, [product, status, error])
}
